import { useEffect, useMemo, useState, useCallback } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Mail, Trash2, Check, Undo2, Search, Globe, Share2, Lock } from "lucide-react";

const SOURCE_META = {
    website: { label: "Website", icon: Globe, cls: "bg-blue-500/15 text-sky-300 ring-blue-500/30" },
    email: { label: "Email", icon: Mail, cls: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30" },
    social: { label: "Social", icon: Share2, cls: "bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-500/30" },
};
const sourceInfo = (s) =>
    SOURCE_META[s] || { label: s ? s[0].toUpperCase() + s.slice(1) : "Other", icon: Mail, cls: "bg-slate-500/15 text-slate-300 ring-slate-500/30" };

export default function MessagesAdmin() {
    const { admin } = useAuth();
    const [messages, setMessages] = useState([]);
    const [source, setSource] = useState("all");
    const [readState, setReadState] = useState("all"); // all | unread | read
    const [query, setQuery] = useState("");

    const load = useCallback(() => api.get("/admin/messages").then((r) => setMessages(r.data)), []);
    useEffect(() => {
        if (admin?.is_owner) load();
    }, [load, admin]);

    const setRead = async (m, read) => {
        await api.patch(`/admin/messages/${m.id}/${read ? "read" : "unread"}`);
        load();
    };
    const remove = async (m) => {
        if (!window.confirm(`Delete message from ${m.name}? This can't be undone.`)) return;
        await api.delete(`/admin/messages/${m.id}`);
        load();
    };

    const sources = useMemo(() => {
        const set = new Set(messages.map((m) => m.source || "website"));
        return ["all", ...Array.from(set)];
    }, [messages]);

    const unreadCount = useMemo(() => messages.filter((m) => !m.read).length, [messages]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return messages.filter((m) => {
            if (source !== "all" && (m.source || "website") !== source) return false;
            if (readState === "unread" && m.read) return false;
            if (readState === "read" && !m.read) return false;
            if (q && !`${m.name} ${m.email}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [messages, source, readState, query]);

    if (admin && !admin.is_owner) {
        return (
            <div data-testid="messages-restricted" className="flex flex-col items-center justify-center py-24 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/15 ring-1 ring-blue-400/30">
                    <Lock className="h-6 w-6 text-sky-300" />
                </div>
                <h1 className="mt-5 font-serif text-2xl text-blue-50">Messages are private</h1>
                <p className="mt-2 max-w-sm text-sm text-slate-300/55">
                    Contact messages from the website are only visible to the practice owner.
                </p>
            </div>
        );
    }

    return (
        <div data-testid="admin-messages">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="font-serif text-3xl text-blue-50">Messages</h1>
                    <p className="mt-1 text-sm text-slate-300/55">
                        Notes families send in. Read messages stay here until you delete them.
                    </p>
                </div>
                <span
                    data-testid="messages-unread-count"
                    className="rounded-full bg-blue-500/15 px-3 py-1 text-xs font-semibold text-sky-300 ring-1 ring-blue-500/30"
                >
                    {unreadCount} unread
                </span>
            </div>

            {/* Filter bar */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap gap-1.5" data-testid="messages-source-filter">
                    {sources.map((s) => {
                        const active = source === s;
                        const label = s === "all" ? "All sources" : sourceInfo(s).label;
                        return (
                            <button
                                key={s}
                                data-testid={`messages-source-${s}`}
                                onClick={() => setSource(s)}
                                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                                    active
                                        ? "bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 text-white"
                                        : "border border-blue-500/25 text-slate-300 hover:bg-blue-500/10"
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex gap-1.5" data-testid="messages-read-filter">
                    {[
                        ["all", "All"],
                        ["unread", "Unread"],
                        ["read", "Read"],
                    ].map(([val, label]) => (
                        <button
                            key={val}
                            data-testid={`messages-read-${val}`}
                            onClick={() => setReadState(val)}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                                readState === val
                                    ? "bg-blue-500/25 text-sky-200 ring-1 ring-blue-400/40"
                                    : "border border-blue-500/25 text-slate-300 hover:bg-blue-500/10"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="relative ml-auto">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400/60" />
                    <input
                        data-testid="messages-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search name or email"
                        className="w-56 rounded-full border border-blue-500/25 bg-[#0c172e] py-2 pl-9 pr-3 text-sm text-slate-200 outline-none placeholder:text-slate-400/50 focus:border-blue-400"
                    />
                </div>
            </div>

            <div className="mt-6 space-y-3">
                {filtered.length === 0 && (
                    <p data-testid="messages-empty" className="text-sm text-slate-300/45">
                        {messages.length === 0 ? "No messages yet." : "No messages match these filters."}
                    </p>
                )}
                {filtered.map((m) => {
                    const info = sourceInfo(m.source || "website");
                    const SrcIcon = info.icon;
                    return (
                        <div
                            key={m.id}
                            data-testid="message-item"
                            className={`rounded-2xl border p-5 ${
                                m.read ? "border-blue-500/12 bg-[#0c172e]" : "border-blue-500/35 bg-[#10203f]"
                            }`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/15 ring-1 ring-blue-500/30">
                                        <Mail className="h-4 w-4 text-blue-400" />
                                    </span>
                                    <div>
                                        <p className="flex flex-wrap items-center gap-2 font-serif text-lg text-slate-300">
                                            {m.name}
                                            <span
                                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${info.cls}`}
                                            >
                                                <SrcIcon className="h-3 w-3" /> {info.label}
                                            </span>
                                            {!m.read && (
                                                <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-sky-400">
                                                    New
                                                </span>
                                            )}
                                        </p>
                                        <a href={`mailto:${m.email}`} className="text-xs text-sky-400/80 hover:underline">
                                            {m.email}
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {m.read ? (
                                        <button
                                            data-testid="message-mark-unread"
                                            onClick={() => setRead(m, false)}
                                            title="Mark unread"
                                            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/80 hover:bg-blue-500/15"
                                        >
                                            <Undo2 className="h-4 w-4" />
                                        </button>
                                    ) : (
                                        <button
                                            data-testid="message-mark-read"
                                            onClick={() => setRead(m, true)}
                                            title="Mark read"
                                            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/80 hover:bg-blue-500/15"
                                        >
                                            <Check className="h-4 w-4" />
                                        </button>
                                    )}
                                    <button
                                        data-testid="message-delete"
                                        onClick={() => remove(m)}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg text-red-300/80 hover:bg-red-500/15"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-300/75">
                                {m.message}
                            </p>
                            <p className="mt-3 text-xs text-slate-300/40">
                                {new Date(m.created_at).toLocaleString()}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
